import { useState } from 'react';

import { Badge, Dropdown } from 'antd';
import { Bell, CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useHeaderNotifications } from '@/features/notifications/hooks/useHeaderNotifications';
import type { HeaderNotificationListItem } from '@/features/notifications/types/headerNotification.types';

/**
 * Nút chuông trên header: danh sách từ seed + cảnh báo mở + job thất bại; đã đọc lưu cục bộ theo user.
 */
export function HeaderNotificationsDropdown() {
  const { t } = useTranslation();
  const { items, unreadCount, markAllRead, onSelectItem } = useHeaderNotifications();
  const [open, setOpen] = useState(false);

  const handleItemClick = (item: HeaderNotificationListItem) => {
    onSelectItem(item);
    setOpen(false);
  };

  const handleMarkAll = () => {
    markAllRead();
  };

  const panel = (
    <div
      style={{
        minWidth: 300,
        maxWidth: 340,
        maxHeight: 400,
        overflow: 'auto',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--toggle-track-border)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {items.length > 0 && (
        <div
          style={{
            padding: '8px 10px',
            display: 'flex',
            justifyContent: 'flex-end',
            borderBottom: '1px solid var(--glass-border)',
            background: 'var(--bg-secondary)',
          }}
        >
          <button
            type="button"
            onClick={handleMarkAll}
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '4px 4px',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <CheckCheck size={12} />
            {t('notifications.header.markAllRead')}
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <div
          style={{
            padding: 12,
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--text-secondary)',
          }}
        >
          {t('notifications.header.empty')}
        </div>
      ) : (
        items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleItemClick(item)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 14px',
              border: 'none',
              borderBottom: '1px solid var(--glass-border)',
              background: item.read ? 'transparent' : 'rgba(59, 130, 246, 0.08)',
              cursor: 'pointer',
              color: 'var(--text-primary)',
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: item.read ? 400 : 600,
                whiteSpace: 'normal',
                lineHeight: 1.35,
              }}
            >
              {item.title}
            </div>
            <div
              style={{
                fontSize: 10,
                color: 'rgba(148, 163, 184, 0.95)',
                marginTop: 4,
              }}
            >
              {item.timeLabel}
            </div>
          </button>
        ))
      )}
    </div>
  );

  const trigger = (
    <button
      type="button"
      aria-label={t('userMenu.notifications')}
      aria-expanded={open}
      title={t('userMenu.notifications')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: 8,
        border: '1px solid var(--toggle-track-border)',
        background: 'var(--bg-secondary)',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        boxShadow: 'var(--shadow-sm)',
        transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <Bell size={18} strokeWidth={2.2} color="currentColor" />
    </button>
  );

  const wrappedTrigger =
    unreadCount > 0 ? (
      <Badge
        count={unreadCount}
        size="small"
        color="#dc2626"
        offset={[-4, 4]}
        overflowCount={9}
        showZero={false}
      >
        {trigger}
      </Badge>
    ) : (
      trigger
    );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      popupRender={() => panel}
    >
      <span style={{ display: 'inline-flex', cursor: 'pointer' }}>{wrappedTrigger}</span>
    </Dropdown>
  );
}
